import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MORTGAGE_CUSTOMER_SERVICE_DISPLAY } from '@ld.consumer-portal/constants';
import { alertIcon, errorIcon, heroiconsWarning } from '@ld.consumer-portal/data-uri';
import { PlatformService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';
import { SvgIconComponent } from '@ld.consumer-portal/ui-shared';

@Component({
  selector: 'ld-error-modal',
  imports: [BaseModalComponent, MatIconModule, SvgIconComponent, TranslocoDirective],
  templateUrl: './error-modal.component.html',
  styleUrl: './error-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorModalComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);

  protected readonly errorIcon = errorIcon;
  protected readonly alertIcon = alertIcon;
  protected readonly heroiconsWarning = heroiconsWarning;

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly defaultTitle = toSignal(this.transloco.selectTranslate('modalsErrorTitle'), { initialValue: '' });
  private readonly defaultMessage = toSignal(this.transloco.selectTranslate('commonAnErrorOccurredPleaseTryAgain'), {
    initialValue: '',
  });
  private readonly defaultPrimaryBtnText = toSignal(this.transloco.selectTranslate('commonButtonsOk1'), {
    initialValue: '',
  });

  readonly isOpen = input<boolean>(false);

  readonly titleInput = input<string | undefined>(undefined, { alias: 'title' });
  protected readonly title = computed(() => this.titleInput() ?? this.defaultTitle());

  readonly messageInput = input<string | undefined>(undefined, { alias: 'message' });
  protected readonly message = computed(() => this.messageInput() ?? this.defaultMessage());

  readonly icon = input<'error' | 'warning'>('error');

  readonly iconPosition = input<'body' | 'header'>('body');

  readonly showCloseButton = input<boolean>(true);

  readonly primaryBtnTextInput = input<string | undefined>(undefined, { alias: 'primaryBtnText' });
  protected readonly primaryBtnText = computed(() => this.primaryBtnTextInput() ?? this.defaultPrimaryBtnText());

  readonly primaryBtnLink = input<string | null>(null);

  readonly secondaryBtnText = input<string | null>(null);

  readonly showSupportContact = input<boolean>(false);

  readonly buttonVariant = input<'purple' | 'neutral'>('purple');

  readonly showHeaderIcon = input<boolean>(false);

  readonly headerIconSrc = input<string | null>(null);

  readonly supportPhone = input<string>(MORTGAGE_CUSTOMER_SERVICE_DISPLAY);

  readonly closed = output<void>();

  readonly primaryClick = output<void>();

  readonly secondaryClick = output<void>();

  protected readonly iconBgClass = computed(() => {
    return this.icon() === 'warning' ? 'bg-yellow-100' : 'bg-red-100';
  });

  protected readonly iconColorClass = computed(() => {
    return this.icon() === 'warning' ? 'text-amber-500' : 'text-red-500';
  });

  protected readonly iconColorValue = computed(() => {
    return this.icon() === 'warning' ? '#f59e0b' : '#ef4444';
  });

  protected readonly headerBgClass = computed(() => {
    return this.icon() === 'warning' ? 'bg-yellow-50' : 'bg-red-50';
  });

  protected readonly hasSecondaryButton = computed(() => {
    return !!this.secondaryBtnText();
  });

  protected readonly primaryBtnClass = computed(() => {
    return this.buttonVariant() === 'neutral' ? 'btn-primary-ld-neutral' : 'btn-primary-ld-purple';
  });

  protected readonly supportPhoneTel = computed(() => {
    return this.supportPhone().replace(/[^0-9]/g, '');
  });

  protected onClose(): void {
    this.closed.emit();
  }

  protected onPrimaryClick(): void {
    this.primaryClick.emit();
  }

  protected onSecondaryClick(): void {
    this.secondaryClick.emit();
  }
}
